import type React from 'react'
import { useEffect, useRef } from 'react'
import { type IStrategyComponentRecord } from '../../models/strategy/strategy-component-types'
import { ChronoUnit, LocalDateTime } from '@js-joda/core'
import { exportData } from '../../pages/strategy/button/save-snapshot-button'

const AUTO_SNAPSHOT_TIMES = [
  { hours: 4, minutes: 0, seconds: 0 },
  { hours: 12, minutes: 0, seconds: 0 },
  { hours: 20, minutes: 0, seconds: 0 },
]

export function useScheduledSnapshot (strategyName: string): React.MutableRefObject<IStrategyComponentRecord> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const strategyDataRef = useRef<IStrategyComponentRecord>({})

  useEffect(() => {
    // Schedule auto-snapshot daily
    scheduleNextAutoSnapshot()

    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  const scheduleNextAutoSnapshot = (): void => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    const now = LocalDateTime.now()

    for (let i = 0; i < AUTO_SNAPSHOT_TIMES.length; i++) {
      const nextTrigger = LocalDateTime.of(
        now.year(), now.month(), now.dayOfMonth(),
        AUTO_SNAPSHOT_TIMES[i].hours, AUTO_SNAPSHOT_TIMES[i].minutes, AUTO_SNAPSHOT_TIMES[i].seconds
      )
      const diff = now.until(nextTrigger, ChronoUnit.MILLIS)

      if (diff > 0) {
        console.debug(`[Strategy] Scheduling next auto-snapshot: ${nextTrigger.toString()} | Now: ${now.toString()} | Diff: ${diff}ms`)
        timeoutRef.current = setTimeout(() => {
          exportData(strategyName, strategyDataRef)
          scheduleNextAutoSnapshot()
        }, diff)
        break
      }

      if (i === AUTO_SNAPSHOT_TIMES.length - 1) {
        const nextTrigger = LocalDateTime.of(
          now.year(), now.month(), now.dayOfMonth(),
          AUTO_SNAPSHOT_TIMES[i].hours, AUTO_SNAPSHOT_TIMES[i].minutes, AUTO_SNAPSHOT_TIMES[i].seconds
        )
        const diff = now.until(nextTrigger, ChronoUnit.MILLIS)
        console.debug(`[Strategy] Scheduling next auto-snapshot: ${nextTrigger.toString()} | Now: ${now.toString()} | Diff: ${diff}`)
        timeoutRef.current = setTimeout(() => {
          exportData(strategyName, strategyDataRef)
          scheduleNextAutoSnapshot()
        }, diff)
        break
      }
    }
  }

  return strategyDataRef
}
